import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InsumoFormModal from './InsumoFormModal';
import imgDefault from '../../imgs/defecto.png';
import {
    obtenerInsumos,
    eliminarInsumo,
    ajusteRapidoStockInsumo
} from '../../services/insumosServicio';

const Insumos = () => {
    const [productos, setProductos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [insumoToEdit, setInsumoToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchInsumos = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await obtenerInsumos();
            setProductos(data || []);
        } catch (error) {
            toast.error("Error al cargar insumos");
            setProductos([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInsumos();
    }, [fetchInsumos]);

    const handleOpenModal = (insumo = null) => {
        setInsumoToEdit(insumo);
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setInsumoToEdit(null);
    };

    const handleSaveInsumo = async () => {
        setModalVisible(false);
        setInsumoToEdit(null);
        await fetchInsumos();
        toast.success("¡Insumo guardado!");
    };

    const handleDeleteInsumo = async (insumo) => {
        if (!window.confirm(`¿Eliminar el insumo "${insumo.nombre}"?`)) return;
        try {
            await eliminarInsumo(insumo.id);
            toast.success("Insumo eliminado");
            fetchInsumos();
        } catch (error) {
            toast.error("No se pudo eliminar el insumo");
        }
    };

    const handleAjusteStock = async (insumo, delta) => {
        if (!insumo.controlar_stock) return;
        const nuevoStock = parseFloat(insumo.stock || 0) + delta;

        let motivo = '';
        if (delta < 0) {
            motivo = window.prompt(`¿Por qué baja el stock de "${insumo.nombre}"? (se registra como merma)`, '');
            if (motivo === null) return; // canceló
        }

        setProductos(prev => prev.map(p => p.id === insumo.id ? { ...p, stock: nuevoStock } : p));
        try {
            await ajusteRapidoStockInsumo(insumo.id, nuevoStock, motivo);
        } catch (error) {
            toast.error("Error al ajustar stock de " + insumo.nombre);
            fetchInsumos();
        }
    };

    const UNIDAD_LABEL = { UN: 'un', KG: 'kg', G: 'g', L: 'l', ML: 'ml' };

    const getSemaforoClass = (p) => {
        if (!p.controlar_stock) return 'badge-soft-secondary text-muted';
        const stock = parseFloat(p.stock || 0);
        const min = parseFloat(p.stock_minimo || 0);
        if (stock <= 0) return 'badge-soft-danger';
        if (stock <= min) return 'badge-soft-warning';
        return 'badge-soft-success';
    };

    const filteredInsumos = productos.filter(p =>
        (p.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="bg-light min-vh-100 py-5 px-3">
            <ToastContainer position="bottom-center" autoClose={2000} hideProgressBar />
            <div className="container" style={{ maxWidth: '1100px' }}>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5">
                    <div>
                        <h1 className="h2 font-weight-bold text-dark mb-1">Insumos</h1>
                        <p className="text-muted">Ingredientes y materias primas que usan tus recetas.</p>
                    </div>
                    <button className="btn btn-primary btn-lg rounded-pill shadow-lg px-4" onClick={() => handleOpenModal(null)}>
                        <i className="fas fa-plus mr-2"></i> Nuevo Insumo
                    </button>
                </div>

                <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                    <div className="card-header bg-white border-0 pt-4 px-4">
                        <div className="input-group bg-light rounded-pill px-3 py-1 border">
                            <div className="input-group-prepend border-0 bg-transparent">
                                <span className="input-group-text bg-transparent border-0 text-muted">
                                    <i className="fas fa-search"></i>
                                </span>
                            </div>
                            <input
                                type="text" className="form-control bg-transparent border-0"
                                placeholder="Buscar insumo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                    <tr>
                                        <th className="border-0 px-4 py-3 text-muted small text-uppercase">Insumo</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase">Costo</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase text-center" style={{ width: '180px' }}>Stock</th>
                                        <th className="border-0 px-4 py-3 text-right text-muted small text-uppercase">Opciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan="4" className="text-center py-5">Cargando insumos...</td></tr>
                                    ) : filteredInsumos.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-5 text-muted">No hay insumos registrados todavía.</td></tr>
                                    ) : (
                                        filteredInsumos.map((p) => (
                                            <tr key={p.id}>
                                                <td className="px-4 py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-light rounded-lg mr-3 shadow-sm" style={{ width: '40px', height: '40px', overflow: 'hidden', flexShrink: 0 }}>
                                                            <img
                                                                src={p.url_imagen || p.img || imgDefault}
                                                                alt=""
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                onError={(e) => { e.target.src = imgDefault; }}
                                                            />
                                                        </div>
                                                        <span className="font-weight-bold text-dark">{p.nombre}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-muted">
                                                    {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(p.costo || 0)}
                                                </td>
                                                <td className="py-3 text-center">
                                                    {p.controlar_stock ? (
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <button className="btn btn-sm btn-light border" onClick={() => handleAjusteStock(p, -1)}>-</button>
                                                            <span className={`badge px-2 py-2 mx-2 rounded-pill shadow-sm ${getSemaforoClass(p)}`} style={{ minWidth: '60px' }}>
                                                                {parseFloat(p.stock || 0)} {UNIDAD_LABEL[p.unidad_medida] || 'un'}
                                                            </span>
                                                            <button className="btn btn-sm btn-light border" onClick={() => handleAjusteStock(p, 1)}>+</button>
                                                        </div>
                                                    ) : (
                                                        <span className="badge badge-soft-secondary px-3 py-2 rounded-pill">S/C</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button className="btn btn-icon btn-light rounded-circle mr-2" onClick={() => handleOpenModal(p)}>
                                                        <i className="fas fa-pen text-primary"></i>
                                                    </button>
                                                    <button className="btn btn-icon btn-light rounded-circle" onClick={() => handleDeleteInsumo(p)}>
                                                        <i className="fas fa-trash text-danger"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .rounded-xl { border-radius: 1.25rem !important; }
                .rounded-lg { border-radius: 0.75rem !important; }
                .badge-soft-success { background-color: #e6fcf5; color: #087f5b; }
                .badge-soft-warning { background-color: #fff3cd; color: #856404; }
                .badge-soft-danger { background-color: #fff5f5; color: #c92a2a; }
                .badge-soft-secondary { background-color: #f8f9fa; color: #6c757d; }
                .btn-icon { width: 38px; height: 38px; display: inline-flex; align-items: center; justify-content: center; border: none; transition: 0.2s; }
                .btn-icon:hover { background-color: #eef2f7; transform: translateY(-2px); }
            `}</style>

            <InsumoFormModal
                isVisible={modalVisible}
                onClose={handleCloseModal}
                onSave={handleSaveInsumo}
                insumoToEdit={insumoToEdit}
            />
        </div>
    );
};

export default Insumos;
